import React, { useState, useEffect } from 'react';
import { Clock, Filter, Download, Eye, User } from 'lucide-react';
import GlassCard from '../../../components/shared/GlassCard';

const AuditTrail = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [availableActions, setAvailableActions] = useState([]);

  useEffect(() => {
    fetchAuditLogs();
    fetchAuditActions();
    fetchAuditStats();
  }, [filterAction, filterUser, startDate, endDate]);

  const fetchAuditLogs = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams();
      if (filterAction) params.append('action', filterAction);
      if (filterUser) params.append('user_id', filterUser);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      params.append('limit', '50');
      
      const response = await fetch(`${backendUrl}/api/rbac/audit/logs?${params}`, {
        headers: {
          'x-user-role': localStorage.getItem('userRole') || 'super_admin'
        }
      });
      const data = await response.json();
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditActions = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/rbac/audit/logs/actions`, {
        headers: {
          'x-user-role': localStorage.getItem('userRole') || 'super_admin'
        }
      });
      const data = await response.json();
      setAvailableActions(data.actions || []);
    } catch (error) {
      console.error('Error fetching actions:', error);
    }
  };

  const fetchAuditStats = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await fetch(`${backendUrl}/api/rbac/audit/stats?${params}`, {
        headers: {
          'x-user-role': localStorage.getItem('userRole') || 'super_admin'
        }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const getActionColor = (action) => {
    const colors = {
      user_login: '#0DA34E',
      user_logout: '#95A5A6',
      user_created: '#417E46',
      user_updated: '#4A90E2',
      user_deleted: '#E74C3C',
      role_changed: '#DF8C4D',
      status_changed: '#F39C12',
      data_accessed: '#3498DB',
      data_created: '#0DA34E',
      data_updated: '#F39C12',
      data_deleted: '#E74C3C',
      approval_approved: '#0DA34E',
      approval_rejected: '#E74C3C',
      settings_changed: '#9B59B6'
    };
    return colors[action] || '#7F8C8D';
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit', 
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  const exportToCsv = () => {
    const headers = ['Timestamp', 'User', 'Action', 'Resource', 'Description', 'IP Address'];
    const rows = logs.map(log => [
      log.timestamp,
      `${log.user_name} (${log.user_email})`,
      log.action,
      `${log.resource_type}${log.resource_id ? ':' + log.resource_id : ''}`,
      log.description,
      log.ip_address || 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text)' }}>
            Audit Trail
          </h1>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>
            Track user actions and system changes
          </p>
        </div>
        <button
          onClick={exportToCsv}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium"
          style={{ 
            backgroundColor: 'var(--g4g-accent)',
            color: 'white'
          }}
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--panel-border)' }}>
            <div className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{stats.total_logs}</div>
            <div className="text-sm" style={{ color: 'var(--muted)' }}>Total Events</div>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--panel-border)' }}>
            <div className="text-2xl font-bold" style={{ color: '#0DA34E' }}>
              {stats.action_counts?.user_login || 0}
            </div>
            <div className="text-sm" style={{ color: 'var(--muted)' }}>User Logins</div>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--panel-border)' }}>
            <div className="text-2xl font-bold" style={{ color: '#417E46' }}>
              {stats.action_counts?.data_created || 0}
            </div>
            <div className="text-sm" style={{ color: 'var(--muted)' }}>Items Created</div>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--panel-border)' }}>
            <div className="text-2xl font-bold" style={{ color: '#F39C12' }}>
              {stats.action_counts?.role_changed || 0}
            </div>
            <div className="text-sm" style={{ color: 'var(--muted)' }}>Role Changes</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <GlassCard className="mb-6 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4" style={{ color: 'var(--muted)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Action</label>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ 
                backgroundColor: 'var(--panel-bg)',
                borderColor: 'var(--panel-border)',
                color: 'var(--text)'
              }}
            >
              <option value="">All Actions</option>
              {availableActions.map(action => (
                <option key={action.value} value={action.value}>{action.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ 
                backgroundColor: 'var(--panel-bg)',
                borderColor: 'var(--panel-border)',
                color: 'var(--text)'
              }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ 
                backgroundColor: 'var(--panel-bg)',
                borderColor: 'var(--panel-border)',
                color: 'var(--text)'
              }}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setFilterAction('');
                setFilterUser('');
                setStartDate('');
                setEndDate('');
              }}
              className="w-full px-4 py-2 rounded-lg border text-sm font-medium"
              style={{ 
                borderColor: 'var(--panel-border)',
                color: 'var(--text)'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </GlassCard>

      {/* Audit Logs Table */}
      <GlassCard>
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-transparent"></div>
            <p className="mt-2 text-sm" style={{ color: 'var(--muted)' }}>Loading audit logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 mx-auto mb-4 opacity-40" style={{ color: 'var(--muted)' }} />
            <p className="text-lg font-medium mb-2" style={{ color: 'var(--text)' }}>No audit logs found</p>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              Try adjusting your filters or check back later
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--panel-border)' }}>
                  <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text)' }}>Timestamp</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text)' }}>User</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text)' }}>Action</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text)' }}>Resource</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text)' }}>Description</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text)' }}>IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr 
                    key={log.id}
                    className="border-b hover:bg-gray-50/50"
                    style={{ borderColor: 'var(--panel-border)' }}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3" style={{ color: 'var(--muted)' }} />
                        <span className="text-xs" style={{ color: 'var(--muted)' }}>
                          {formatTimestamp(log.timestamp)}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <User className="w-3 h-3" style={{ color: 'var(--muted)' }} />
                        <div>
                          <div className="text-sm font-medium" style={{ color: 'var(--text)' }}>{log.user_name}</div>
                          <div className="text-xs" style={{ color: 'var(--muted)' }}>{log.user_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span 
                        className="inline-block px-2 py-1 rounded text-xs font-medium"
                        style={{ 
                          backgroundColor: `${getActionColor(log.action)}15`,
                          color: getActionColor(log.action)
                        }}
                      >
                        {log.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm font-mono" style={{ color: 'var(--text)' }}>
                        {log.resource_type}
                        {log.resource_id && <span style={{ color: 'var(--muted)' }}>:{log.resource_id.slice(0, 8)}</span>}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm" style={{ color: 'var(--text)' }}>{log.description}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-xs font-mono" style={{ color: 'var(--muted)' }}>
                        {log.ip_address || '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
    </div>
  );
};

export default AuditTrail;
